import React from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import { media } from '@this/components/shared/atoms/media';
import { MidButton } from '@this/components/shared/atoms/button';
import Tooltip from '@this/shared/tooltip/tooltip';
import { Link } from '@this/shared/ui/navigations/link';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Header, Date, OutlineListRail, OutlineRailContent } from '../../outline_item/outline_item.template';

import type SearchQuery from '../../../../../domain/search_query';
import type SearchQueryItem from '../../../../../domain/search_query_item';
import type SelectStore from '../../../../../domain/select_store';
import SearchBlock from '../../../../shared/search/search_block/search_block';
import SelectOutlineItem from '../../outline_item/outline_item';

interface Props {
  // Select.props
  query: SearchQuery;
  store: SelectStore;
  selectErrors: string[];
  submitting: boolean;

  handleSelectItem: () => void;

  handleAddItem?: (item_type: 'transport' | 'hotel', e: React.MouseEvent<HTMLElement>) => void;
  handleAdditionalSearch: (queryItem: SearchQueryItem, e: React.FormEvent<HTMLFormElement>) => void;
  additionalSearchErrors?: { [k in string]: string };
  handleRemoveItem?: () => void;

  handleNextButtonClick: (e: React.MouseEvent<HTMLElement>) => void;
  handleOpenShowSearchBox: () => void;

  // ArrangementRequestForm
  isRequestForm: boolean;
}

const SelectOutlineTemplate: React.SFC<Props> = ({
  query,
  store,
  selectErrors,

  handleSelectItem,

  handleAddItem,
  handleRemoveItem,
  handleAdditionalSearch,
  additionalSearchErrors,

  handleNextButtonClick,
  submitting,

  isRequestForm,
  handleOpenShowSearchBox
}) => (
  <Outline isRequestForm={isRequestForm}>
    <OutlineListRail>
      {(store.showPackage && store.result.type === 'airPackage'
        ? store.result.packageItems[store.result.airline]
        : store.showPackage && store.result.type === 'jrPackage'
        ? store.result.jrPackageItems
        : store.result.items
      ).map((item, i) => (
        <SelectOutlineItem
          railLayout
          key={item.index}
          icon={i + 1}
          item={item}
          handleClickTab={store.handleClickTab}
          handleSelectItem={handleSelectItem}
          selected={store.currentTab === item.index}
          hotelPriceLimit={item.hotelPriceLimit}
          store={store}
          direction={store.result.type !== 'airPackage' ? '' : i === 0 ? 'out' : i === 2 ? 'home' : ''}
          isRequestForm={isRequestForm}
          handleOpenShowSearchBox={handleOpenShowSearchBox}
        />
      ))}

      {store.result.type === 'separate' &&
        handleAddItem &&
        store.result.isMultipleLocationsSearchResult() &&
        (store.result.items.length === store.query.items.length ? (
          <ItemBlock className="select-outline__item-block">
            <OutlineRailContent>
              <AddButtonWrapper>
                {store.result.transportAvailable && (
                  <AddItemLink
                    type="submit"
                    disabled={query.isItemLimit()}
                    onClick={e => handleAddItem('transport', e)}
                  >
                    +交通機関を追加
                  </AddItemLink>
                )}
                {store.result.hotelAvailable && (
                  <AddItemLink
                    type="submit"
                    disabled={query.isItemLimit()}
                    onClick={e => handleAddItem('hotel', e)}
                  >
                    +ホテルを追加
                  </AddItemLink>
                )}
              </AddButtonWrapper>
            </OutlineRailContent>
          </ItemBlock>
        ) : store.result.items.length < store.query.items.length ? (
          <ItemBlock className="select-outline__item-block">
            <OutlineRailContent>
              <form
                className="search"
                onSubmit={e => handleAdditionalSearch(store.query.items[store.query.items.length - 1], e)}
              >
                <SearchBlock item={store.query.items[store.query.items.length - 1]} />
                <div className="search__button-wrapper">
                  {additionalSearchErrors && (
                    <div className="search__error">
                      {Object.keys(additionalSearchErrors).map((key, i) => (
                        <div className="error" key={i}>
                          {additionalSearchErrors[key]}
                        </div>
                      ))}
                    </div>
                  )}
                  <ButtonGroup>
                    <Button type="submit" disabled={submitting}>
                      検索
                    </Button>
                    <Link onClick={handleRemoveItem}>キャンセル</Link>
                  </ButtonGroup>
                </div>
              </form>
            </OutlineRailContent>
          </ItemBlock>
        ) : null)}

      <ItemBlock className="select-outline__item-block">
        <Header $alignToRail $packageTotalRow={store.result.type !== 'separate'}>
          <ArrowIcon>↑</ArrowIcon>
          {store.result.type === 'separate' ? (
            <Date>
              合計金額：
              <Price>
                <span data-wovn-ignore>{utils.digits(store.result.getReservingTrip().totalPrice())}</span>
                <Yen>円</Yen>
              </Price>
              <Tooltip type="info">
                <div className="select__tooltip-format">
                  {store.result
                    .getReservingTrip()
                    .tooltipText()
                    .split('\n')
                    .map((l: string, i: number) => (
                      <p key={i}>{l}</p>
                    ))}
                </div>
              </Tooltip>
            </Date>
          ) : (
            <PackagePriceArea>
              <Wrap>
                <Content>
                  <PackageTitle>
                    パッケージプラン
                    <Tooltip type="info">
                      <div className="select__tooltip-format">
                        {store.result
                          .getPackageTrip()
                          .tooltipText()
                          .split('\n')
                          .map((l: string, i: number) => (
                            <p key={i}>{l}</p>
                          ))}
                      </div>
                    </Tooltip>
                  </PackageTitle>
                  <Price>
                    合計：
                    <PriceMain data-wovn-ignore>
                      {utils.digits(store.result.getPackageTrip().totalPrice())}
                    </PriceMain>{' '}
                    円 (税込)
                  </Price>
                </Content>
              </Wrap>
            </PackagePriceArea>
          )}
        </Header>
      </ItemBlock>
    </OutlineListRail>

    {!isRequestForm && (
      <>
        <ReserveButton
          className={`select-pc__reserve-button ${
            submitting || store.result.isAirUnavailable(store.unavailableTransitIds) ? 'loading' : ''
          }`}
          onClick={handleNextButtonClick}
          aria-disabled={submitting || store.result.isAirUnavailable(store.unavailableTransitIds)}
          style={
            submitting || store.result.isAirUnavailable(store.unavailableTransitIds)
              ? { pointerEvents: 'none' }
              : undefined
          }
        >
          予約確認画面へ
        </ReserveButton>
        {store.result.isAirUnavailable(store.unavailableTransitIds) && (
          <div className="select__errors-bottom">
            <div className="error">選択中の便は予約できません。他の便を選択してください。</div>
          </div>
        )}
        {selectErrors.length > 0 && (
          <div className="select__errors-bottom">
            {selectErrors.map((error, i) => (
              <div className="error" key={i}>
                {error}
              </div>
            ))}
          </div>
        )}
      </>
    )}
  </Outline>
);

const Outline = styled.div<{ isRequestForm: boolean }>`
  padding: ${props => (props.isRequestForm ? '15px 10px' : '10px 0 60px')};
`;

const Price = styled.span`
  color: ${props => props.theme.redColor};
  vertical-align: text-bottom;
  display: inline;

  > span:first-of-type {
    ${media.sp`
      font-size: 18px;
      font-weight: bold;
      line-height: 1;
    `}
  }
`;

const Yen = styled.span`
  font-size: 9px;
  margin-left: 3px;
`;

const PriceMain = styled.span`
  font-size: 18px;
`;

const ReserveButton = styled.div`
  ${MidButton}
  display: none;
  position: relative;
  width: 214px;
  margin: 40px auto;
  ${media.sp`
    display: block;
  `}
`;

const PackagePriceArea = styled.div`
  display: flex;
  flex: 1;
  height: 77px;
  background: #f6f4ee;
`;

const PackageTitle = styled.div`
  font-size: 11px;
  display: flex;
  margin-bottom: 5px;
  justify-content: center;
  color: #222222;
`;

const Content = styled.div`
  margin: auto;
`;

const Wrap = styled.div`
  display: flex;
  font-weight: bold;
  width: 100%;
`;

export const ButtonGroup = styled.div`
  display: flex;
  align-items: center;

  *:not(:first-child) {
    margin-left: 10px;
  }
`;

const ItemBlock = styled.div`
  ${media.sp`
    padding: 0 12px 0 4px;
  `}
`;

export const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${props => props.theme.iconColor};
  border-radius: 50%;
  color: white;
  font-weight: bold;
  font-size: 14px;
  line-height: 1;
  margin-right: 10px;
  position: relative;
  z-index: 2;
`;

export const AddButtonWrapper = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;

  > * {
    flex: 1 1 0;
    min-width: 0;
  }
`;

export const AddItemLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  border: 2px solid ${props => props.theme.linkColor};
  border-radius: 3px;
  padding: 6px 8px;
  font-weight: bold;
  letter-spacing: 0.04em;
  font-size: 14px;
  text-align: center;
`;

export default SelectOutlineTemplate;
